/**
 * The URL state of a list (design §5.5, ED-36): `view`, `q`, `f`, `sort`, `cols` and `page`.
 * A parameter is only in the URL when it differs from the view, and `f` REPLACES the filter of
 * the view (so a condition of the view can be removed). Reading never throws: a broken part is
 * left out and `filterBroken` says so, so the list still opens.
 */
import { decodeFilter, encodeFilter, normalizeFilter, sameFilter } from './codec'
import type { Condition, Filter, Register, ViewDef } from './types'
import { isUuid } from './validate'

export const DEFAULT_VIEW = 'sys:alle'

/** The order the parameters are written in. */
const PARAM_ORDER = ['view', 'q', 'f', 'sort', 'cols', 'page'] as const
export type ListParam = (typeof PARAM_ORDER)[number]
/** Parameters of the E1 pages, turned into a filter and then dropped. */
const LEGACY: readonly string[] = ['label_id', 'category_id', 'archief']

const MAX_PAGE = 10_000
const MIN_SEARCH = 2
const MAX_SEARCH = 200

/** What the URL says; the view decides what is left unsaid. */
export interface ListUrl {
  view: string | null
  /** The trimmed search term; '' when it is shorter than two characters. */
  q: string
  /** The decoded `f`; null when absent or broken. */
  filter: Filter | null
  /** `f` is there, but is not a filter we can read. */
  filterBroken: boolean
  sort: string | null
  cols: string[] | null
  page: number
}

export function readListUrl(params: URLSearchParams): ListUrl {
  const f = params.get('f')
  const filter = f ? decodeFilter(f) : null
  const q = (params.get('q') ?? '').trim()
  const page = Number(params.get('page'))
  const cols = (params.get('cols') ?? '')
    .split(',')
    .map((key) => key.trim())
    .filter(Boolean)
  return {
    view: params.get('view') || null,
    q: q.length < MIN_SEARCH ? '' : q.slice(0, MAX_SEARCH),
    filter,
    filterBroken: Boolean(f) && filter === null,
    sort: params.get('sort') || null,
    cols: cols.length > 0 ? cols : null,
    page: Number.isInteger(page) && page >= 1 && page <= MAX_PAGE ? page : 1,
  }
}

/** `null` or '' removes a parameter; leaving it out keeps it. */
export type ListChanges = Partial<Record<ListParam, string | null>>

/**
 * The parameters after a change, rebuilt in the fixed order (C's pattern): the page goes back to
 * 1 (is removed) unless the change is the page itself, and the legacy parameters and any other
 * parameter than the six of the list are dropped, so a stale one never lingers.
 */
export function withListChanges(params: URLSearchParams, changes: ListChanges): URLSearchParams {
  const next = new URLSearchParams()
  for (const key of PARAM_ORDER) {
    const change = changes[key]
    const value = change !== undefined ? change : key === 'page' ? null : params.get(key)
    if (value) next.set(key, value)
  }
  return next
}

/** A URL without any parameter: the only one where the last used view is opened. */
export function isBareUrl(params: URLSearchParams): boolean {
  return [...params.keys()].length === 0
}

/**
 * The filter for the E1 links (`label_id`, `category_id`, `archief=1`) on top of the filter of
 * the default view; `null` when the URL has none of them.
 */
export function legacyFilter(params: URLSearchParams, base: Filter): Filter | null {
  if (!LEGACY.some((key) => params.has(key))) return null
  const archived = params.get('archief') === '1'
  const quick: Condition[] = base.quick.filter((condition) => !(archived && condition.property === 'status'))
  const label = params.get('label_id')?.toLowerCase()
  const category = params.get('category_id')?.toLowerCase()
  if (isUuid(label)) quick.push({ property: 'labels', operator: 'contains_any_of', value: [label] })
  if (isUuid(category)) quick.push({ property: 'category', operator: 'is_any_of', value: [category] })
  return normalizeFilter({ quick, groups: base.groups })
}

export function viewSort(view: ViewDef, register: Register): string {
  return view.sort ?? register.default_sort
}

export function viewColumns(view: ViewDef, register: Register): string[] {
  return view.columns.length > 0 ? view.columns : register.default_columns
}

/**
 * The columns that exist, once each, with the fixed column first; [] when none of them exist. The
 * column picker starts from the same list the table shows.
 */
export function cleanColumns(columns: readonly string[], register: Register): string[] {
  const shown = register.properties.filter((property) => property.column !== null)
  const known = new Set(shown.map((property) => property.key))
  const chosen = [...new Set(columns)].filter((key) => known.has(key))
  if (chosen.length === 0) return []
  const fixed = shown.filter((property) => property.column?.fixed).map((property) => property.key)
  return [...fixed, ...chosen.filter((key) => !fixed.includes(key))]
}

/**
 * The columns a view shows: its own that still exist, else those of the object. A view can list
 * columns that are gone (a property removed in a later release); it must not end up with none,
 * not even the fixed one.
 */
function columnsOfView(view: ViewDef, register: Register): string[] {
  const own = cleanColumns(viewColumns(view, register), register)
  return own.length > 0 ? own : cleanColumns(register.default_columns, register)
}

function isSortable(sort: string, register: Register, extra: readonly string[]): boolean {
  const key = sort.replace(/^-/, '')
  return extra.includes(key) || register.properties.some((property) => property.key === key && property.sortable)
}

const sameList = (a: readonly string[], b: readonly string[]): boolean => a.length === b.length && a.every((key, index) => key === b[index])

/** What the list shows: the URL where it says something that holds, the view for the rest. */
export interface EffectiveState {
  filter: Filter
  sort: string
  columns: string[]
  q: string
  page: number
  /** The filter, sort or columns differ from the view ("Weergave gewijzigd"; search and page do not count). */
  modified: boolean
}

/** `extraSortKeys`: sorts that exist on this page only (a list's Toegevoegd), not in the register. */
export function effectiveState(url: ListUrl, view: ViewDef, register: Register, extraSortKeys: readonly string[] = []): EffectiveState {
  const filter = url.filter ?? normalizeFilter(view.query)
  const baseSort = viewSort(view, register)
  const sort = url.sort !== null && isSortable(url.sort, register, extraSortKeys) ? url.sort : baseSort
  const baseColumns = columnsOfView(view, register)
  const fromUrl = url.cols ? cleanColumns(url.cols, register) : []
  const columns = fromUrl.length > 0 ? fromUrl : baseColumns
  return {
    filter,
    sort,
    columns,
    q: url.q,
    page: url.page,
    modified: !sameFilter(filter, view.query) || sort !== baseSort || !sameList(columns, baseColumns),
  }
}

/**
 * The `f` to write for a filter, or null when it is the filter of the view. Past `MAX_F_LENGTH`
 * the link would read as broken: check `fitsInUrl(next)` first and refuse a filter that does not.
 */
export function filterParam(next: Filter, view: ViewDef): string | null {
  return sameFilter(next, view.query) ? null : encodeFilter(next)
}

/** The `sort` to write, or null when it is the sort of the view. */
export function sortParam(next: string, view: ViewDef, register: Register): string | null {
  return next === viewSort(view, register) ? null : next
}

/** The `cols` to write, or null when the columns are those of the view. */
export function colsParam(next: readonly string[], view: ViewDef, register: Register): string | null {
  return sameList(next, columnsOfView(view, register)) ? null : next.join(',')
}
